import React, { useEffect, useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { X, Download, Share, Plus } from 'lucide-react';

const STORAGE_KEY = 'demarre_pwa_dismissed';
const DISMISS_DAYS = 14; // re-show after 2 weeks if dismissed

const isStandalone = () =>
  typeof window !== 'undefined' &&
  (window.matchMedia?.('(display-mode: standalone)').matches ||
   window.navigator.standalone === true);

const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

const isAdminRoute = () => typeof window !== 'undefined' && window.location.pathname.startsWith('/admin');

const PWAInstallPrompt = () => {
  const { language } = useLanguage();
  const t = (pt, en, es) => language === 'en' ? en : language === 'es' ? es : pt;

  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [showIOSModal, setShowIOSModal] = useState(false);

  useEffect(() => {
    if (isStandalone() || isAdminRoute()) return;

    // Check dismissed flag
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const ts = parseInt(raw, 10);
        if (!Number.isNaN(ts) && (Date.now() - ts) < DISMISS_DAYS * 86400000) return;
      }
    } catch {}

    // Android / Desktop Chrome path
    const handleBefore = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Wait 5s of engagement before showing the banner
      setTimeout(() => setShowBanner(true), 5000);
    };
    window.addEventListener('beforeinstallprompt', handleBefore);

    // iOS Safari fallback (no beforeinstallprompt)
    if (isIOS()) {
      // Wait 8s for user to engage with the page before nudging
      const id = setTimeout(() => setShowBanner(true), 8000);
      return () => {
        window.removeEventListener('beforeinstallprompt', handleBefore);
        clearTimeout(id);
      };
    }

    return () => window.removeEventListener('beforeinstallprompt', handleBefore);
  }, []);

  const handleInstall = async () => {
    if (isIOS()) {
      setShowIOSModal(true);
      return;
    }
    if (!deferredPrompt) return;
    try {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice.outcome === 'accepted') {
        setShowBanner(false);
      }
      setDeferredPrompt(null);
    } catch {
      setShowBanner(false);
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch {}
  };

  if (!showBanner && !showIOSModal) return null;

  return (
    <>
      {showBanner && (
        <div
          className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-96 z-[60] rounded-2xl border border-demarre-200 bg-white shadow-2xl p-4 animate-in slide-in-from-bottom-4 duration-500"
          data-testid="pwa-install-banner"
        >
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-demarre-500 to-demarre-700 flex items-center justify-center text-white shadow-md">
              <Download className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-demarre-900 text-sm">
                {t('Instalar Demarré Tours', 'Install Demarré Tours', 'Instalar Demarré Tours')}
              </p>
              <p className="text-xs text-gray-600 mt-0.5">
                {t(
                  'Acesso rápido na tela inicial e funciona offline.',
                  'Quick home screen access and works offline.',
                  'Acceso rápido en la pantalla de inicio y funciona offline.'
                )}
              </p>
              <div className="flex items-center gap-2 mt-3">
                <Button
                  size="sm"
                  className="bg-demarre-600 hover:bg-demarre-700 text-white h-8 text-xs px-3"
                  onClick={handleInstall}
                  data-testid="pwa-install-accept"
                >
                  {t('Instalar', 'Install', 'Instalar')}
                </Button>
                <button
                  onClick={handleDismiss}
                  className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1"
                  data-testid="pwa-install-dismiss"
                >
                  {t('Mais tarde', 'Later', 'Más tarde')}
                </button>
              </div>
            </div>
            <button
              onClick={handleDismiss}
              className="flex-shrink-0 w-7 h-7 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-600"
              aria-label="close"
              data-testid="pwa-install-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {showIOSModal && (
        <div
          className="fixed inset-0 z-[70] bg-black/50 flex items-end sm:items-center justify-center p-4"
          onClick={() => setShowIOSModal(false)}
          data-testid="pwa-ios-modal"
        >
          <div
            className="bg-white rounded-2xl w-full max-w-md p-5 shadow-2xl animate-in slide-in-from-bottom-8 duration-300"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-demarre-900 text-base">
                {t('Como instalar no iPhone', 'How to install on iPhone', 'Cómo instalar en iPhone')}
              </h3>
              <button
                onClick={() => setShowIOSModal(false)}
                className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-500"
                data-testid="pwa-ios-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <ol className="space-y-3 text-sm text-gray-700">
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-demarre-100 text-demarre-700 font-bold text-xs flex items-center justify-center">1</span>
                <span className="flex-1">
                  {t('Toque no botão', 'Tap the', 'Toque el botón')}{' '}
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-blue-50 border border-blue-200 text-blue-700">
                    <Share className="w-3.5 h-3.5" /> {t('Compartilhar', 'Share', 'Compartir')}
                  </span>{' '}
                  {t('na barra do Safari (abaixo).', 'button in Safari (below).', 'en la barra de Safari (abajo).')}
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-demarre-100 text-demarre-700 font-bold text-xs flex items-center justify-center">2</span>
                <span className="flex-1">
                  {t('Role para baixo e selecione', 'Scroll down and choose', 'Desplácese y elija')}{' '}
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-gray-50 border border-gray-200 text-gray-700">
                    <Plus className="w-3.5 h-3.5" /> {t('Adicionar à Tela de Início', 'Add to Home Screen', 'Añadir a Pantalla de Inicio')}
                  </span>
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-demarre-100 text-demarre-700 font-bold text-xs flex items-center justify-center">3</span>
                <span className="flex-1">
                  {t(
                    'Confirme em "Adicionar". Pronto — o app aparece na sua tela inicial.',
                    'Confirm with "Add". Done — the app will appear on your home screen.',
                    'Confirme en "Añadir". Listo — la app aparecerá en su pantalla de inicio.'
                  )}
                </span>
              </li>
            </ol>
            <Button
              className="w-full mt-5 bg-demarre-600 hover:bg-demarre-700"
              onClick={() => { setShowIOSModal(false); handleDismiss(); }}
            >
              {t('Entendi', 'Got it', 'Entendido')}
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default PWAInstallPrompt;
